import React, { useEffect, useMemo, useState } from "react";
import { getFirestore, collection, getDocs, query, orderBy } from "firebase/firestore";

const db = getFirestore();

export default function CodePromoPage() {
  const [countToGenerate, setCountToGenerate] = useState(500);
  const [loadingGen, setLoadingGen] = useState(false);
  const [error, setError] = useState("");

  const [codes25, setCodes25] = useState([]);
  const [codes15, setCodes15] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const fetchCodes = async () => {
    try {
      setLoadingList(true);
      setError("");

      const q25 = query(
        collection(db, "code_promos", "25", "codes"),
        orderBy("createdAt", "desc")
      );
      const q15 = query(
        collection(db, "code_promos", "15", "codes"),
        orderBy("createdAt", "desc")
      );

      const [snap25, snap15] = await Promise.all([getDocs(q25), getDocs(q15)]);

      setCodes25(snap25.docs.map((d) => d.data()));
      setCodes15(snap15.docs.map((d) => d.data()));
    } catch (e) {
      console.error(e);
      setError("Erreur de lecture Firestore.");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, []);

  const stats25 = useMemo(() => {
    const total = codes25.length;
    const used = codes25.filter((c) => c.used).length;
    return { total, used, remaining: total - used };
  }, [codes25]);

  const stats15 = useMemo(() => {
    const total = codes15.length;
    const used = codes15.filter((c) => c.used).length;
    return { total, used, remaining: total - used };
  }, [codes15]);

  const copyList = async (codes) => {
    const txt = codes.map((c) => c.code).join("\n");
    await navigator.clipboard.writeText(txt);
    alert("✅ Copié dans le presse-papier !");
  };

  const generateCodes = async () => {
    try {
      setLoadingGen(true);
      setError("");

      const n = Number(countToGenerate);
      if (!Number.isInteger(n) || n < 200 || n > 700) {
        setError("⚠️ Le nombre doit être entre 200 et 700.");
        return;
      }

      const res = await fetch(
        "https://us-central1-mendyx-adafe.cloudfunctions.net/generatePromoCodes",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ count: n }),
        }
      );

      const data = await res.json();
      if (!data.ok) throw new Error(data?.error || "Erreur génération");

      await fetchCodes();
    } catch (e) {
      console.error(e);
      setError("Erreur génération codes (Cloud Function).");
    } finally {
      setLoadingGen(false);
    }
  };

  const list25 = useMemo(() => codes25.map((c) => c.code).join("\n"), [codes25]);
  const list15 = useMemo(() => codes15.map((c) => c.code).join("\n"), [codes15]);

  return (
    <div className="container mx-auto py-10 px-4 max-w-6xl">
      <h1 className="text-3xl font-bold text-[#4c2f8e] mb-6">Admin - Codes promo</h1>

      {/* Génération */}
      <div className="bg-white rounded-2xl shadow border p-6 mb-8">
        <h2 className="text-xl font-semibold text-[#4c2f8e] mb-3">
          Générer des codes (25€ + 15€)
        </h2>

        <div className="flex flex-col md:flex-row gap-3 items-center">
          <input
            type="number"
            min={200}
            max={700}
            value={countToGenerate}
            onChange={(e) => setCountToGenerate(Number(e.target.value))}
            className="border rounded-lg px-4 py-2 w-full md:w-48"
            placeholder="500"
          />
          <button
            onClick={generateCodes}
            disabled={loadingGen}
            className="px-5 py-2 rounded-full text-white bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] shadow hover:scale-105 transition disabled:opacity-60"
          >
            {loadingGen ? "Génération..." : `Créer ${countToGenerate} codes (x2)`}
          </button>

          <button
            onClick={fetchCodes}
            className="px-5 py-2 rounded-full border border-[#4c2f8e] text-[#4c2f8e] hover:bg-[#f6f0ff] transition"
          >
            Rafraîchir
          </button>
        </div>

        {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-2xl shadow border p-6">
          <h3 className="text-lg font-semibold text-[#4c2f8e]">Section 25€</h3>
          <p className="text-sm text-gray-600 mt-2">
            Total : <strong>{stats25.total}</strong> • Utilisés : <strong>{stats25.used}</strong> • Restants :{" "}
            <strong>{stats25.remaining}</strong>
          </p>
          <button
            onClick={() => copyList(codes25)}
            className="mt-4 px-4 py-2 rounded-full bg-[#4c2f8e] text-white hover:bg-[#351f6a] transition"
          >
            Copier la liste 25€
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow border p-6">
          <h3 className="text-lg font-semibold text-[#4c2f8e]">Section 15€</h3>
          <p className="text-sm text-gray-600 mt-2">
            Total : <strong>{stats15.total}</strong> • Utilisés : <strong>{stats15.used}</strong> • Restants :{" "}
            <strong>{stats15.remaining}</strong>
          </p>
          <button
            onClick={() => copyList(codes15)}
            className="mt-4 px-4 py-2 rounded-full bg-[#4c2f8e] text-white hover:bg-[#351f6a] transition"
          >
            Copier la liste 15€
          </button>
        </div>
      </div>

      {/* Affichage copie/collé */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow border p-6">
          <h3 className="text-lg font-semibold text-[#4c2f8e] mb-3">
            Codes 25€ (copier/coller)
          </h3>

          {loadingList ? (
            <p className="text-gray-500">Chargement...</p>
          ) : (
            <textarea
              value={list25}
              readOnly
              className="w-full h-[450px] border rounded-xl p-3 font-mono text-sm"
            />
          )}
        </div>

        <div className="bg-white rounded-2xl shadow border p-6">
          <h3 className="text-lg font-semibold text-[#4c2f8e] mb-3">
            Codes 15€ (copier/coller)
          </h3>

          {loadingList ? (
            <p className="text-gray-500">Chargement...</p>
          ) : (
            <textarea
              value={list15}
              readOnly
              className="w-full h-[450px] border rounded-xl p-3 font-mono text-sm"
            />
          )}
        </div>
      </div>
    </div>
  );
}
